import { useState, useEffect, useRef, useCallback } from "react";
import { Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS, LinearScale, PointElement, LineElement,
  Tooltip, Legend,
} from "chart.js";
import { getSprintDados, clearCache, updateTicketEstimativas } from "../api/sprint.api";
import { getSetupSprint } from "../api/setup.api";

// Registra apenas os elementos necessários — ChartDataLabels NÃO é usado
// no scatter (evita conflito com o registro global feito no SprintHistorico)
ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend);

const FECHADOS_STATUS = new Set([
  "Fechado", "Resolvido", "Em Teste", "Feedback",
  "Aguardando Testes", "Aguardando Versão", "Ajuste Especificação",
]);

const r2 = v => v !== null && v !== undefined ? Math.round(v * 100) / 100 : null;

/* Converte decimal de horas para formato H:MM (igual ao Redmine)
   Ex: 1.1666... → "1:10h"  |  2.5 → "2:30h"  |  3.0 → "3h" */
function formatHoras(h) {
  if (h === null || h === undefined) return null;
  const horas = Math.floor(h);
  const mins  = Math.round((h - horas) * 60);
  if (mins === 0) return `${horas}h`;
  return `${horas}:${String(mins).padStart(2, "0")}h`;
}

/* Normaliza os campos de horas — spent_hours mantém precisão total para formatHoras */
function normalizarTicket(t) {
  const spentRaw = parseFloat(t.spent_hours ?? t.tempo_gasto);
  return {
    ...t,
    e_estimado_analise: r2(parseFloat(t.e_estimado_analise) || null),
    e_a1:               r2(parseFloat(t.e_a1)               || null),
    spent_hours:        isNaN(spentRaw) ? null : spentRaw, // precisão total — formatar na exibição
  };
}

/* ── Cores por desenvolvedor ── */
const DEV_COLORS = [
  "#22d3ee","#4ade80","#a78bfa","#fbbf24",
  "#f87171","#60a5fa","#fb923c","#34d399",
];
function devColorMap(devs) {
  const map = {};
  [...new Set(devs)].forEach((n, i) => { map[n] = DEV_COLORS[i % DEV_COLORS.length]; });
  return map;
}

function desvio(estimado, real) {
  if (!estimado || !real) return null;
  return Math.round(((real - estimado) / estimado) * 100);
}

function acuracia(tickets) {
  const validos = tickets.filter(t => t.e_estimado_analise && t.spent_hours);
  if (!validos.length) return null;
  const corretos = validos.filter(t => Math.abs(desvio(t.e_estimado_analise, t.spent_hours)) <= 20);
  return Math.round((corretos.length / validos.length) * 100);
}

const REFRESH_SEC = 300; // 5 minutos

export default function SprintEstimativas() {
  const [tickets,       setTickets]       = useState([]);
  const [sprint,        setSprint]        = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [semDados,      setSemDados]      = useState(false);
  const [countdown,     setCountdown]     = useState(REFRESH_SEC);
  const [modalTicket,   setModalTicket]   = useState(null); // ticket selecionado para edição
  const loadRef = useRef(null);

  const load = useCallback((bust = false) => {
    if (bust) clearCache();
    setLoading(true);
    Promise.allSettled([getSprintDados(), getSetupSprint()]).then(([t, s]) => {
      const raw = t.status === "fulfilled" ? (t.value || []) : [];
      const normalizado = raw.map(normalizarTicket);
      setTickets(normalizado);
      if (s.status === "fulfilled") setSprint(s.value);
      const temEstimativas = normalizado.some(tk => tk.e_estimado_analise || tk.e_a1 || tk.spent_hours);
      setSemDados(!temEstimativas);
      setLoading(false);
    });
  }, []);

  loadRef.current = load;

  function handleRefresh() {
    loadRef.current(true);
    setCountdown(REFRESH_SEC);
  }

  useEffect(() => {
    loadRef.current(true);
    setCountdown(REFRESH_SEC);
    const id = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { loadRef.current(true); return REFRESH_SEC; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "40vh", color: "#475569" }}>
      Carregando estimativas...
    </div>
  );

  const sprintLabel = sprint?.grupo_entrega || sprint?.versao || "Sprint Atual";

  /* ── Tickets com pelo menos algum campo de estimativa ── */
  const comDados = tickets.filter(t =>
    t.e_estimado_analise || t.e_a1 || t.spent_hours
  );

  /* ── KPIs ── */

  const avgPlanej = comDados.filter(t => t.e_estimado_analise).length
    ? comDados.reduce((s, t) => s + (t.e_estimado_analise || 0), 0) / comDados.filter(t => t.e_estimado_analise).length
    : null;
  const avgDev = comDados.filter(t => t.e_a1).length
    ? comDados.reduce((s, t) => s + (t.e_a1 || 0), 0) / comDados.filter(t => t.e_a1).length
    : null;
  const avgReal = comDados.filter(t => t.spent_hours).length
    ? comDados.reduce((s, t) => s + (t.spent_hours || 0), 0) / comDados.filter(t => t.spent_hours).length
    : null;
  const acc = acuracia(tickets);

  /* ── Scatter data: E_Estimado_Analise vs Real ── */
  const colorMap = devColorMap(comDados.map(t => t.desenvolvedor || "—"));
  const scatterData = comDados
    .filter(t => t.e_estimado_analise && t.spent_hours)
    .map(t => ({
      x: t.e_estimado_analise,
      y: t.spent_hours,
      dev: t.desenvolvedor || "—",
      id: t.id,
      label: `#${t.id}`,
    }));

  const maxVal = scatterData.length
    ? Math.ceil(Math.max(...scatterData.map(d => Math.max(d.x, d.y))) * 1.1)
    : 10;

  const chartData = {
    datasets: [
      /* Linha de referência (estimativa perfeita) */
      {
        type: "line",
        label: "Estimativa perfeita",
        data: [{ x: 0, y: 0 }, { x: maxVal, y: maxVal }],
        borderColor: "rgba(96,165,250,0.3)",
        borderWidth: 1.5,
        borderDash: [6, 4],
        pointRadius: 0,
        pointHoverRadius: 0,
      },
      /* Pontos por desenvolvedor */
      ...Object.entries(colorMap).map(([nome, cor]) => ({
        type: "scatter",
        label: nome,
        data: scatterData.filter(d => d.dev === nome).map(d => ({
          x: d.x, y: d.y, ticketId: d.id,
        })),
        backgroundColor: `${cor}cc`,
        borderColor: cor,
        borderWidth: 1.5,
        pointRadius: 8,
        pointHoverRadius: 10,
      })),
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 16, right: 16 } },
    plugins: {
      legend: { labels: { color: "#94a3b8", font: { size: 11 }, padding: 14 } },
      datalabels: { display: false }, // desativa globalmente para este chart
      tooltip: {
        backgroundColor: "rgba(8,18,42,0.95)",
        titleColor: "#e2e8f0", bodyColor: "#94a3b8",
        borderColor: "rgba(96,165,250,0.2)", borderWidth: 1,
        callbacks: {
          title: ctx => ctx[0]?.datasetIndex === 0 ? "Linha de referência" : `#${ctx[0]?.raw?.ticketId ?? ""}`,
          label: ctx => {
            if (ctx.datasetIndex === 0) return " Estimativa perfeita";
            const d = ctx.raw;
            const dv = desvio(d.x, d.y);
            return [
              ` Planejado: ${d.x}h`,
              ` Real: ${d.y}h`,
              ` Desvio: ${dv !== null ? (dv >= 0 ? "+" : "") + dv + "%" : "—"}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        min: 0, max: maxVal,
        title: { display: true, text: "Estimativa de análise (h)", color: "#475569", font: { size: 11 } },
        ticks: { color: "#475569" },
        grid: { color: "rgba(96,165,250,0.06)" },
      },
      y: {
        min: 0, max: maxVal,
        title: { display: true, text: "Tempo real gasto (h)", color: "#475569", font: { size: 11 } },
        ticks: { color: "#475569" },
        grid: { color: "rgba(96,165,250,0.06)" },
      },
    },
  };

  /* ── Ranking de acurácia por developer ── */
  const devsMap = {};
  tickets.forEach(t => {
    if (!t.desenvolvedor) return;
    if (!devsMap[t.desenvolvedor]) devsMap[t.desenvolvedor] = { nome: t.desenvolvedor, total: 0, comEstimativa: 0, somaDesvio: 0 };
    devsMap[t.desenvolvedor].total++;
    if (t.e_estimado_analise && t.spent_hours) {
      devsMap[t.desenvolvedor].comEstimativa++;
      devsMap[t.desenvolvedor].somaDesvio += Math.abs(desvio(t.e_estimado_analise, t.spent_hours));
    }
  });
  const rankingDevs = Object.values(devsMap)
    .filter(d => d.comEstimativa > 0)
    .map(d => ({ ...d, desvioMedio: Math.round(d.somaDesvio / d.comEstimativa) }))
    .sort((a, b) => a.desvioMedio - b.desvioMedio);

  /* ── Tabela de tickets ── */
  const tabelaTickets = tickets
    .filter(t => t.e_estimado_analise || t.e_a1 || t.spent_hours)
    .map(t => ({
      id:       t.id,
      subject:  t.subject || "—",
      dev:      t.desenvolvedor || "—",
      programado: t.programado === "1" || t.programado === 1 || t.programado === true,
      status:   t.status || "—",
      fechado:  FECHADOS_STATUS.has(t.status),
      planej:   t.e_estimado_analise ?? null,
      devEst:   t.e_a1              ?? null,
      real:     t.spent_hours       ?? null,
      desvPlanej: desvio(t.e_estimado_analise, t.spent_hours),
      desvDev:    desvio(t.e_a1, t.spent_hours),
    }))
    // Ordenação: com estimativa primeiro, depois por nome do desenvolvedor
    .sort((a, b) => {
      const aTemEst = a.planej !== null ? 1 : 0;
      const bTemEst = b.planej !== null ? 1 : 0;
      if (bTemEst !== aTemEst) return bTemEst - aTemEst;
      return a.dev.localeCompare(b.dev);
    });

  async function handleSaveEstimativa(ticketId, valores) {
    await updateTicketEstimativas(ticketId, valores);
    // Atualiza localmente sem re-fetch para UX imediata
    setTickets(prev => prev.map(t => t.id === ticketId ? {
      ...t,
      e_estimado_analise: r2(Number(valores.e_estimado_analise) || t.e_estimado_analise),
      e_a1:               r2(Number(valores.e_a1)               || t.e_a1),
    } : t));
    setModalTicket(null);
  }

  return (
    <div style={page}>

      {/* ── Modal de edição de estimativas ── */}
      {modalTicket && (
        <TicketEstimativaModal
          ticket={modalTicket}
          onClose={() => setModalTicket(null)}
          onSave={handleSaveEstimativa}
        />
      )}

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <h1 style={pageTitle}>Análise de Estimativas</h1>
          <div style={pageSub}>Sprint {sprintLabel} — comparativo entre estimativa de análise, estimativa do desenvolvedor e tempo real</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <button
            onClick={handleRefresh}
            title="Atualizar agora"
            style={refreshBtn}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(96,165,250,0.18)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(96,165,250,0.08)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.2)"; }}
          >↻</button>
          <CountdownRing seconds={countdown} total={REFRESH_SEC} />
        </div>
      </div>

      {/* ── Aviso se sem dados ── */}
      {semDados && (
        <div style={{
          padding: "16px 20px", borderRadius: 12,
          background: "rgba(251,191,36,0.07)", border: "1px solid rgba(251,191,36,0.25)",
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#fbbf24", marginBottom: 4 }}>
            Aguardando dados de estimativa
          </div>
          <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>
            Os campos customizados <strong style={{ color: "#94a3b8" }}>E_Estimado_Analise</strong> e <strong style={{ color: "#94a3b8" }}>E_A1</strong> ainda não estão chegando no payload da API para esta sprint.
            O campo <strong style={{ color: "#94a3b8" }}>Tempo gasto</strong> é um campo nativo do Redmine.
            Verifique se o endpoint <code style={{ color: "#60a5fa" }}>/sprint/dados</code> utiliza o mesmo método que recebeu os LEFT JOINs (<code style={{ color: "#60a5fa" }}>SprintRepository::getIssues()</code>).
          </div>
        </div>
      )}

      {/* ── KPIs ── */}
      <div style={kpiGrid}>
        <KpiCard label="Estimativa média (análise)" value={avgPlanej ? formatHoras(avgPlanej) : "—"} cor="#22d3ee" />
        <KpiCard label="Estimativa média (dev)"     value={avgDev    ? formatHoras(avgDev)    : "—"} cor="#a78bfa" />
        <KpiCard label="Tempo real médio"            value={avgReal   ? formatHoras(avgReal)   : "—"} cor="#60a5fa" />
        <KpiCard label="Acurácia (desvio ≤ 20%)"    value={acc !== null ? `${acc}%` : "—"} cor={acc === null ? "#475569" : acc >= 70 ? "#4ade80" : acc >= 50 ? "#fbbf24" : "#f87171"} big />
      </div>

      {/* ── Scatter: Estimado vs Real ── */}
      <div style={card}>
        <div style={cardTitle}>Planejado vs Real — cada ponto é um ticket</div>
        <div style={{ fontSize: 11, color: "#475569", marginBottom: 14 }}>
          Pontos <span style={{ color: "#f87171" }}>acima da linha</span> = subestimado (demorou mais).
          Pontos <span style={{ color: "#4ade80" }}>abaixo da linha</span> = superestimado (foi mais rápido).
        </div>
        {scatterData.length > 0 ? (
          <div style={{ height: 320 }}>
            <Scatter key="scatter-estimativas" data={chartData} options={chartOptions} />
          </div>
        ) : (
          <EmptyChart label="Sem dados de estimativa vs real disponíveis" />
        )}
      </div>

      {/* ── Ranking de acurácia ── */}
      {rankingDevs.length > 0 && (
        <div style={card}>
          <div style={cardTitle}>Ranking de acurácia por desenvolvedor</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {rankingDevs.map((d, i) => {
              const cor = d.desvioMedio <= 20 ? "#4ade80" : d.desvioMedio <= 50 ? "#fbbf24" : "#f87171";
              const barW = Math.min(100, Math.max(4, 100 - d.desvioMedio));
              return (
                <div key={d.nome} style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 14px", borderRadius: 10, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(96,165,250,0.07)" }}>
                  <span style={{ fontSize: 13, color: "#475569", fontWeight: 700, width: 20 }}>{i + 1}</span>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#e2e8f0" }}>{d.nome}</span>
                  <span style={{ fontSize: 11, color: "#475569" }}>{d.comEstimativa} ticket{d.comEstimativa > 1 ? "s" : ""}</span>
                  <div style={{ width: 120 }}>
                    <div style={{ height: 6, background: "rgba(255,255,255,0.05)", borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${barW}%`, background: cor, borderRadius: 3, transition: "width 0.6s" }} />
                    </div>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 800, color: cor, width: 60, textAlign: "right" }}>
                    {d.desvioMedio <= 20 ? `+${d.desvioMedio}%` : `±${d.desvioMedio}%`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Análise por tipo e projeto ── */}
      <AnaliseGrupos tickets={tickets} />

      {/* ── Tabela de tickets ── */}
      <div style={card}>
        <div style={cardTitle}>Detalhes por ticket — ordenado pelo maior desvio</div>
        <div style={{ fontSize: 11, color: "#475569", marginBottom: 12, padding: "8px 12px", borderRadius: 8, background: "rgba(96,165,250,0.05)", border: "1px solid rgba(96,165,250,0.1)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>
            Origem: <strong style={{ color: "#94a3b8" }}>todos os tickets da sprint atual</strong> — inclui programados (◎) e emergências (⚡).
            Apenas tickets com pelo menos um dos três campos preenchidos são exibidos.
          </span>
          <span style={{ fontWeight: 700, color: "#60a5fa", whiteSpace: "nowrap", marginLeft: 16 }}>
            {tabelaTickets.length} ticket{tabelaTickets.length !== 1 ? "s" : ""}
          </span>
        </div>
        {tabelaTickets.length > 0 ? (
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "6%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "33%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "10%" }} />
            </colgroup>
            <thead>
              <tr>
                <th style={th}>Ticket</th>
                <th style={th}>Tipo</th>
                <th style={th}>Título</th>
                <th style={th}>Desenvolvedor</th>
                <th style={{ ...th, textAlign: "center" }}>Estimativa de Análise</th>
                <th style={{ ...th, textAlign: "center" }}>Estimativa do Desenvolvedor</th>
                <th style={{ ...th, textAlign: "center" }}>Tempo Real</th>
                <th style={{ ...th, textAlign: "center" }}>Desvio do Planejamento</th>
                <th style={{ ...th, textAlign: "center" }}>Desvio do Desenvolvedor</th>
              </tr>
            </thead>
            <tbody>
              {tabelaTickets.map((t, i) => {
                const isProg = t.programado;
                return (
                  <tr
                    key={i}
                    onClick={() => setModalTicket(t)}
                    style={{
                      background: i % 2 === 0 ? "rgba(96,165,250,0.03)" : "transparent",
                      cursor: "pointer",
                      transition: "background 0.12s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(96,165,250,0.08)"}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "rgba(96,165,250,0.03)" : "transparent"}
                  >
                    <td style={{ ...td, color: "#60a5fa", fontWeight: 700, whiteSpace: "nowrap" }}>#{t.id}</td>
                    <td style={{ ...td }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5, whiteSpace: "nowrap",
                        color: isProg ? "#22d3ee" : "#a78bfa",
                        background: isProg ? "rgba(34,211,238,0.1)" : "rgba(167,139,250,0.1)",
                        border: `1px solid ${isProg ? "rgba(34,211,238,0.25)" : "rgba(167,139,250,0.25)"}`,
                      }}>{isProg ? "◎ Programado" : "⚡ Emergência"}</span>
                    </td>
                    <td style={{ ...td }} title={t.subject}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
                        <span style={{
                          flexShrink: 0, fontSize: 10, fontWeight: 700,
                          padding: "1px 7px", borderRadius: 4,
                          color: t.fechado ? "#4ade80" : "#fbbf24",
                          background: t.fechado ? "rgba(74,222,128,0.08)" : "rgba(251,191,36,0.08)",
                          border: `1px solid ${t.fechado ? "rgba(74,222,128,0.2)" : "rgba(251,191,36,0.2)"}`,
                        }}>{t.fechado ? "Fechado" : "Aberto"}</span>
                        <span style={{ fontSize: 12, color: "#e2e8f0", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {t.subject}
                        </span>
                      </div>
                    </td>
                    <td style={{ ...td, color: "#94a3b8", whiteSpace: "nowrap" }}>{t.dev}</td>
                    <td style={{ ...td, textAlign: "center" }}>{t.planej !== null ? formatHoras(t.planej) : <span style={{ color: "#334155" }}>—</span>}</td>
                    <td style={{ ...td, textAlign: "center" }}>{t.devEst !== null ? formatHoras(t.devEst) : <span style={{ color: "#334155" }}>—</span>}</td>
                    <td style={{ ...td, textAlign: "center" }}>{t.real   !== null ? formatHoras(t.real)   : <span style={{ color: "#334155" }}>—</span>}</td>
                    <td style={{ ...td, textAlign: "center" }}><DesvioChip v={t.desvPlanej} /></td>
                    <td style={{ ...td, textAlign: "center" }}><DesvioChip v={t.desvDev} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div style={{ color: "#475569", fontSize: 12, padding: "20px 0", textAlign: "center" }}>
            Nenhum ticket com dados de estimativa disponíveis ainda.
          </div>
        )}
      </div>

    </div>
  );
}

/* ─── Sub-componentes ─────────────────────────────────────────────── */

function KpiCard({ label, value, cor, big }) {
  return (
    <div style={kpiCard}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
      <div style={{ fontSize: big ? 30 : 24, fontWeight: 900, color: cor, lineHeight: 1.1, marginTop: 6 }}>{value}</div>
    </div>
  );
}

function DesvioChip({ v }) {
  if (v === null || v === undefined) return <span style={{ color: "#334155" }}>—</span>;
  const cor = Math.abs(v) <= 20 ? "#4ade80" : Math.abs(v) <= 50 ? "#fbbf24" : "#f87171";
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 6,
      fontSize: 11, fontWeight: 800, color: cor,
      background: `${cor}15`, border: `1px solid ${cor}40`,
    }}>
      {v >= 0 ? "+" : ""}{v}%
    </span>
  );
}

function EmptyChart({ label }) {
  return (
    <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", border: "1px dashed rgba(96,165,250,0.1)", borderRadius: 10 }}>
      <span style={{ fontSize: 12, color: "#334155" }}>{label}</span>
    </div>
  );
}


/* ─── Modal de edição de estimativas ─────────────────────────────── */

const REDMINE_URL = "https://redmine.lexsis.com.br/issues";

function TicketEstimativaModal({ ticket, onClose, onSave }) {
  const [planej, setPlanej] = useState(ticket.planej !== null ? String(ticket.planej) : "");
  const [devEst, setDevEst] = useState(ticket.devEst !== null ? String(ticket.devEst) : "");
  const [saving, setSaving] = useState(false);
  const [erro,   setErro]   = useState(null);
  const isProg = ticket.programado;

  async function handleSave() {
    setSaving(true);
    setErro(null);
    try {
      await onSave(ticket.id, { e_estimado_analise: planej, e_a1: devEst });
    } catch {
      setErro("Endpoint ainda não implementado no backend. Veja o prompt gerado abaixo.");
      setSaving(false);
    }
  }

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(6,13,30,0.75)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: 480, background: "rgba(11,22,42,0.97)", border: "1px solid rgba(96,165,250,0.18)", borderRadius: 16, padding: "24px 26px", display: "flex", flexDirection: "column", gap: 18, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}
      >
        {/* Cabeçalho */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: "#60a5fa" }}>#{ticket.id}</span>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 5,
                color: isProg ? "#22d3ee" : "#a78bfa",
                background: isProg ? "rgba(34,211,238,0.1)" : "rgba(167,139,250,0.1)",
                border: `1px solid ${isProg ? "rgba(34,211,238,0.25)" : "rgba(167,139,250,0.25)"}`,
              }}>{isProg ? "◎ Programado" : "⚡ Emergência"}</span>
            </div>
            <div style={{ fontSize: 13, color: "#e2e8f0", fontWeight: 600, lineHeight: 1.4 }}>{ticket.subject}</div>
            <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>Desenvolvedor: {ticket.dev}</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#475569", fontSize: 18, cursor: "pointer", lineHeight: 1, padding: 4 }}>✕</button>
        </div>

        {/* Tempo real (somente leitura) */}
        <div style={{ padding: "12px 14px", borderRadius: 10, background: "rgba(96,165,250,0.05)", border: "1px solid rgba(96,165,250,0.1)" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 4 }}>Tempo Real Gasto</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#60a5fa" }}>{ticket.real !== null ? formatHoras(ticket.real) : "—"}</div>
          <div style={{ fontSize: 10, color: "#334155", marginTop: 2 }}>Registrado via apontamentos de horas no Redmine — somente leitura</div>
        </div>

        {/* Campos editáveis */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {[
            { label: "Estimativa de Análise (h)", value: planej, set: setPlanej, hint: "Preenchida pelo responsável pela sprint" },
            { label: "Estimativa do Desenvolvedor — E_A1 (h)", value: devEst, set: setDevEst, hint: "Preenchida pelo desenvolvedor ao receber o ticket" },
          ].map(({ label, value, set, hint }) => (
            <div key={label} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</label>
              <input
                type="number" min="0" step="0.5"
                value={value}
                onChange={e => set(e.target.value)}
                placeholder="Ex: 2.5"
                style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid rgba(96,165,250,0.2)", background: "rgba(11,22,42,0.7)", color: "#e2e8f0", fontSize: 14, outline: "none", fontFamily: "Inter, sans-serif" }}
                disabled={saving}
              />
              <span style={{ fontSize: 10, color: "#334155" }}>{hint}</span>
            </div>
          ))}
        </div>

        {/* Erro */}
        {erro && (
          <div style={{ padding: "9px 12px", borderRadius: 8, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)", fontSize: 12, color: "#f87171" }}>
            ⚠ {erro}
          </div>
        )}

        {/* Ações */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
          <a
            href={`${REDMINE_URL}/${ticket.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 12, color: "#60a5fa", textDecoration: "none", display: "flex", alignItems: "center", gap: 5 }}
          >
            ↗ Abrir no Redmine
          </a>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} disabled={saving} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(96,165,250,0.15)", background: "transparent", color: "#475569", fontSize: 12, cursor: "pointer" }}>
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid rgba(96,165,250,0.4)", background: "rgba(96,165,250,0.15)", color: "#60a5fa", fontSize: 12, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}>
              {saving ? "Salvando..." : "Salvar estimativas"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Análise por Tipo e Projeto ─────────────────────────────────── */

function grupoStats(tickets) {
  const total    = tickets.length;
  const comEst   = tickets.filter(t => t.e_estimado_analise && t.spent_hours);
  if (!comEst.length) return { total, desvioMedio: null, acuracia: null };
  const desvios  = comEst.map(t => Math.abs(desvio(t.e_estimado_analise, t.spent_hours)));
  const medio    = Math.round(desvios.reduce((s, v) => s + v, 0) / desvios.length);
  const corretos = desvios.filter(v => v <= 20).length;
  return {
    total,
    comEstimativa: comEst.length,
    desvioMedio: medio,
    acuracia: Math.round((corretos / desvios.length) * 100),
  };
}

function AnaliseGrupos({ tickets }) {
  const comDados = tickets.filter(t => t.e_estimado_analise || t.spent_hours);
  if (!comDados.length) return null;

  // Por tipo
  const progs  = comDados.filter(t => t.programado === "1" || t.programado === 1 || t.programado === true);
  const emrgs  = comDados.filter(t => !(t.programado === "1" || t.programado === 1 || t.programado === true));
  const porTipo = [
    { label: "◎ Programados", cor: "#22d3ee", ...grupoStats(progs) },
    { label: "⚡ Emergências", cor: "#a78bfa", ...grupoStats(emrgs) },
  ].filter(g => g.total > 0);

  // Por projeto (extrai prefixo [Cliente] do subject)
  const projetoMap = {};
  comDados.forEach(t => {
    const match = (t.subject || "").match(/^\[([^\]]+)\]/);
    const proj  = match ? match[1] : "Sem projeto";
    if (!projetoMap[proj]) projetoMap[proj] = [];
    projetoMap[proj].push(t);
  });
  const porProjeto = Object.entries(projetoMap)
    .map(([label, tks]) => ({ label, cor: "#60a5fa", ...grupoStats(tks) }))
    .filter(g => g.comEstimativa > 0)
    .sort((a, b) => (b.desvioMedio ?? 0) - (a.desvioMedio ?? 0))
    .slice(0, 8);

  function GrupoRow({ label, cor, total, comEstimativa, desvioMedio, acuracia }) {
    const corBar = acuracia == null ? "#475569" : acuracia >= 70 ? "#4ade80" : acuracia >= 50 ? "#fbbf24" : "#f87171";
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderBottom: "1px solid rgba(96,165,250,0.05)" }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: cor, minWidth: 180 }}>{label}</span>
        <span style={{ fontSize: 11, color: "#475569", minWidth: 80 }}>{total} ticket{total !== 1 ? "s" : ""}</span>
        {acuracia != null ? (
          <>
            <div style={{ flex: 1, height: 5, background: "rgba(255,255,255,0.05)", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${acuracia}%`, background: corBar, borderRadius: 3, transition: "width 0.6s" }} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 800, color: corBar, width: 50, textAlign: "right" }}>{acuracia}%</span>
            <span style={{ fontSize: 11, color: "#475569", width: 70, textAlign: "right" }}>±{desvioMedio}% desvio</span>
          </>
        ) : (
          <span style={{ fontSize: 11, color: "#334155", marginLeft: 8 }}>sem dados de estimativa vs real</span>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
      <div style={card}>
        <div style={cardTitle}>Acurácia por tipo de ticket</div>
        {porTipo.map(g => <GrupoRow key={g.label} {...g} />)}
      </div>
      <div style={card}>
        <div style={cardTitle}>Acurácia por projeto — mais problemáticos primeiro</div>
        {porProjeto.length > 0
          ? porProjeto.map(g => <GrupoRow key={g.label} {...g} />)
          : <div style={{ fontSize: 12, color: "#334155" }}>Sem dados suficientes.</div>}
      </div>
    </div>
  );
}

/* ─── CountdownRing ───────────────────────────────────────────────── */

function CountdownRing({ seconds, total }) {
  const r      = 12;
  const circ   = 2 * Math.PI * r;
  const filled = circ * (seconds / total);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#475569", fontSize: 11, fontWeight: 600 }}>
      <svg width={30} height={30} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
        <circle cx={15} cy={15} r={r} fill="none" stroke="rgba(96,165,250,0.12)" strokeWidth={2.5} />
        <circle cx={15} cy={15} r={r} fill="none" stroke="#60a5fa" strokeWidth={2.5}
          strokeDasharray={`${filled} ${circ}`} strokeLinecap="round"
          style={{ transition: "stroke-dasharray 1s linear" }} />
      </svg>
      <span>{String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}</span>
    </div>
  );
}

/* ─── Estilos ─────────────────────────────────────────────────────── */

const refreshBtn = {
  width: 34, height: 34, borderRadius: 8,
  border: "1px solid rgba(96,165,250,0.2)",
  background: "rgba(96,165,250,0.08)",
  color: "#60a5fa", fontSize: 18, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  transition: "background 0.15s, border-color 0.15s",
  flexShrink: 0, lineHeight: 1,
};

const page      = { padding: "28px 32px", display: "flex", flexDirection: "column", gap: 20 };
const pageTitle = { color: "#f1f5f9", fontSize: 22, fontWeight: 700, margin: 0 };
const pageSub   = { color: "#475569", fontSize: 13, marginTop: 4 };
const kpiGrid   = { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 };
const kpiCard   = {
  background: "rgba(11,22,42,0.6)", border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 12, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 4,
};
const card = {
  background: "rgba(11,22,42,0.6)", border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 14, padding: "20px 22px",
};
const cardTitle = { fontSize: 11, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 12 };
const th = { padding: "10px 10px", fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5, textAlign: "left", borderBottom: "1px solid rgba(96,165,250,0.1)", lineHeight: 1.4 };
const td = { padding: "12px 10px", fontSize: 12, color: "#94a3b8", borderBottom: "1px solid rgba(96,165,250,0.05)", verticalAlign: "middle" };
